import { Directive, effect, inject, input, ViewChild, type Signal } from '@angular/core';
import { AfterViewInit, ChangeDetectorRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { UIChart } from 'primeng/chart';
import { IDatasource, Datasource as DatasourceCtor } from 'ngx-ui-scroll';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { SharedService } from '../../shared/services/shared.service';
import { ClientStatsService } from '../services/stats/client-stats.service';
import { ConfirmationService } from 'primeng/api';
import { CustomLoggerService } from '../../services/monitor/custom-logger.service';
import { CursorPaginationState, SortDir } from '../helpers/cursor-pagination-state';

// Re-exported for use in concrete components
export type { SortDir } from '../helpers/cursor-pagination-state';
export type AdapterOf<T> = InstanceType<typeof DatasourceCtor<T>>['adapter'];
export type ChartKind = 'bar' | 'line' | 'pie';

/**
 * Abstract base class for all client-stats section components
 * (clickthroughs, pref-changes, enrollments, …).
 *
 * Contains every piece of code shared by the concrete section components:
 *  - Signal inputs (key, params, parent control signals)
 *  - Service injections
 *  - Shared state (criteria, UI flags, chart, virtual-scroll datasource)
 *  - Angular lifecycle hooks (ngAfterViewInit)
 *  - All effects (_mode, _chartType, params-change)
 *  - Cursor-pagination via CursorPaginationState
 *  - toggleSort, onRowClick, chart utilities, confirmation-dialog helpers
 *
 * Concrete classes only need to implement the four abstract methods.
 */
@Directive()
export abstract class ClientStatsSectionBase<TRow extends object>
  implements OnInit, OnDestroy, AfterViewInit {

  // ── Signal inputs ────────────────────────────────────────────────────────
  key               = input<string>('');
  params            = input<Record<string, any> | undefined>();
  /** Parent pushes 'Summary' | 'Details' into this signal. */
  subcomponetModeSignal = input<Signal<string>>();
  /** Parent pushes chart-type string into this signal. */
  chartTypeSignal       = input<Signal<string>>();

  // ── Service injections ───────────────────────────────────────────────────
  protected readonly sharedService       = inject(SharedService);
  protected readonly clientStatsService  = inject(ClientStatsService);
  protected readonly confirmationService = inject(ConfirmationService);
  protected readonly customLoggerService = inject(CustomLoggerService);
  protected readonly cdr                 = inject(ChangeDetectorRef);
  protected readonly ngZone              = inject(NgZone);

  // ── Selection criteria ───────────────────────────────────────────────────
  selectedBrandCode!:    string;
  selectedBrandName!:    string;
  baseUrl!:              string;
  selectedDateFrom:      string | undefined;
  selectedDateTo:        string | undefined;

  // ── UI state ─────────────────────────────────────────────────────────────
  isSummaryShown  = true;
  isDetailsShown  = false;
  tabValue: 'Summary' | 'Details' = 'Summary';
  hasData         = false;
  isSpinnerHidden = true;

  // ── Chart ─────────────────────────────────────────────────────────────────
  @ViewChild('chart') chart!: UIChart;
  chartData:    any;
  chartOptions: any;

  chartTypes: { label: string; value: ChartKind }[] = [
    { label: 'Bar',      value: 'bar' },
    // { label: 'Doughnut', value: 'doughnut' },
    { label: 'Line',     value: 'line' },
    { label: 'Pie',      value: 'pie' },
  ];
  selectedChartType: 'bar' | 'line' | 'pie' = 'bar';

  // ── Summary table ─────────────────────────────────────────────────────────
  summaryRows:    any[] = [];
  summaryColumns: any[] = [];

  // ── Details virtual scroll ────────────────────────────────────────────────
  datasource!:   IDatasource<TRow> & { adapter: AdapterOf<TRow> };
  sortField!:    string;
  sortDirection: SortDir = 'asc';
  selectedRow:   TRow | null = null;

  filterName$       = new Subject<string>();
  currentFilterName = '';

  // ── Cursor pagination ────────────────────────────────────────────────────
  protected readonly pagination = new CursorPaginationState();

  // ── PDF export ────────────────────────────────────────────────────────────
  protected pdfSectionTitle = '';

  // ── Effects ──────────────────────────────────────────────────────────────

  /** Reacts to parent signal switching between Summary and Details views. */
  readonly _mode = effect(() => {
    const s = this.subcomponetModeSignal();
    if (!s) return;
    const v = s();
    if (v === 'Summary') {
      this.isSummaryShown = true;
      this.isDetailsShown = false;
      this.tabValue       = 'Summary';
    } else if (v === 'Details') {
      this.isSummaryShown = false;
      this.isDetailsShown = true;
      this.tabValue       = 'Details';
    } else {
      console.error('Invalid tab value from parent signal:', v);
    }
  });

  /** Reacts to parent signal changing the chart type. */
  readonly _chartType = effect(() => {
    const s = this.chartTypeSignal();
    if (!s) return;
    const v = s();
    if (v === 'bar' || v === 'line' || v === 'pie') {
      this.selectedChartType = v;
      this.chartOptions = this.getOptionsForType(this.selectedChartType);
    } else {
      console.error('Invalid chart type from parent signal:', v);
    }
  });

  /** Re-fetches data whenever key or params change. */
  private readonly _paramsEffect = effect(() => {
    const k = this.key();
    if (!k) return;
    const p = this.params();
    this.onParamsChanged(k, p);
  });

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /** Subclasses must set up summaryColumns, sortField, and call initDatasource(). */
  abstract ngOnInit(): void;

  ngOnDestroy(): void {}

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  // ── Params handling ───────────────────────────────────────────────────────

  /**
   * Called whenever key/params signal changes. Extracts the common criteria
   * fields from params. Subclasses may override to read extra fields, but must
   * call super.onParamsChanged(key, params) first.
   */
  protected onParamsChanged(key: string, params?: Record<string, any>): void {
    console.log(`onParamsChanged - key: ${key}, params: ${JSON.stringify(params)}`);
    if (params) {
      this.selectedBrandCode    = params['brandCode'] || '';
      this.selectedBrandName    = params['brandName'] || '';
      this.baseUrl          = params['baseUrl']   || '';
      this.selectedDateFrom = params['dateFrom']  || '';
      this.selectedDateTo   = params['dateTo']    || '';
    }
    this.fetchData();
  }

  // ── Abstract methods ──────────────────────────────────────────────────────

  /** Fetch summary data from the backend and populate summaryRows + chartData. */
  abstract fetchData(): void;

  /** Initialize the ngx-ui-scroll Datasource for the Details tab. */
  abstract initDatasource(): void;

  /** Return true if the given row is the currently selected one. */
  abstract isSelected(row: TRow): boolean;

  // ── Shared methods ────────────────────────────────────────────────────────

  exportToPdf(): void {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(14);
    doc.text(this.pdfSectionTitle, 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text(`Brand: ${this.selectedBrandName}`, 14, 23);
    doc.text(`Date: ${this.selectedDateFrom ?? ''} – ${this.selectedDateTo ?? ''}`, pageWidth - 14, 23, { align: 'right' });
    doc.setTextColor(0);

    const cols        = this.summaryColumns.filter((c: any) => c.display !== 'none');
    const tableStartY = 28;
    const singlePage  = this.summaryRows.length <= 10;
    const canvas      = this.hasData
      ? (this.chart as any)?.chart?.canvas as HTMLCanvasElement | undefined
      : undefined;

    if (singlePage) {
      // Table left half, chart right half — everything on one page
      const chartX  = 157;
      const chartW  = pageWidth - chartX - 14;
      let tableEndY = tableStartY;
      autoTable(doc, {
        head: [cols.map((c: any) => c.header as string)],
        body: this.summaryRows.map((r: any) => cols.map((c: any) => String(r[c.field] ?? ''))),
        startY: tableStartY,
        margin: { left: 14, right: 155 },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [39, 38, 221] },
        didDrawPage: (data) => { tableEndY = data.cursor?.y ?? tableEndY; }
      });
      if (canvas) {
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', chartX, tableStartY, chartW, chartW * 0.7);
      }
    } else {
      // Table full-width, chart below or on next page
      let tableEndY = tableStartY;
      autoTable(doc, {
        head: [cols.map((c: any) => c.header as string)],
        body: this.summaryRows.map((r: any) => cols.map((c: any) => String(r[c.field] ?? ''))),
        startY: tableStartY,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [39, 38, 221] },
        didDrawPage: (data) => { tableEndY = data.cursor?.y ?? tableEndY; }
      });
      if (canvas) {
        const imgData = canvas.toDataURL('image/png');
        const imgW    = pageWidth - 28;
        const imgH    = imgW * 0.45;
        if (tableEndY + imgH + 16 > pageHeight) {
          doc.addPage();
          doc.addImage(imgData, 'PNG', 14, 14, imgW, imgH);
        } else {
          doc.addImage(imgData, 'PNG', 14, tableEndY + 10, imgW, imgH);
        }
      }
    }

    const safeName = this.pdfSectionTitle.replace(/\s+/g, '_').toLowerCase();
    doc.save(`${safeName}_${this.selectedDateFrom ?? ''}_${this.selectedDateTo ?? ''}.pdf`);
  }

  onChartTypeChanged(_data: any): void {
    this.chartOptions = this.getOptionsForType(this.selectedChartType);
    this.cdr.detectChanges();
    this.ngZone.onStable.pipe(take(1)).subscribe(() => setTimeout(() => this.chart?.refresh()));
  }

  toggleSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField     = field;
      this.sortDirection = 'asc';
    }
    this.pagination.clear();
    this.pagination.seedForSort(this.sortDirection);
    console.log(`toggleSort: field=${this.sortField}, direction=${this.sortDirection}`);
    this.datasource.adapter.reload(0);
  }

  onRowClick(row: TRow): void {
    this.selectedRow = row;
  }

  // ── Protected helpers ─────────────────────────────────────────────────────

  protected getOptionsForType(type: string): any {
    const base: any = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend:  { position: 'top' },
        tooltip: { enabled: true }
      }
    };
    if (type === 'bar' || type === 'line' || type === 'radar') {
      base.scales = {
        x: { stacked: false, ticks: { autoSkip: true } },
        y: { beginAtZero: true, ticks: { precision: 0 } }
      };
    } else {
      delete base.scales;
    }
    if (type === 'line') {
      base.elements = { line: { tension: 0.3 }, point: { radius: 3 } };
    }
    return base;
  }

  /** Trigger Angular change detection and chart refresh after data loads. */
  protected refreshChart(): void {
    if (this.tabValue === 'Summary') {
      this.cdr.detectChanges();
      this.afterDomStabilizes(() => this.chart?.refresh());
    }
  }

  protected afterDomStabilizes(fn: () => void): void {
    this.ngZone.onStable.pipe(take(1)).subscribe(() => setTimeout(fn));
  }

  protected showMissingCriteriaWarning(message?: string): void {
    this.confirmationService.confirm({
      message: message ??
        'Make sure to select a brand and the date interval in the selection criteria to retrieve database records.',
      header: 'Warning',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Ok',
      rejectVisible: false,
      acceptButtonStyleClass: 'p-button-info wal-info p-button-rounded',
      key: 'client-dashboard-warning',
      accept: () => { return; }
    });
  }

  protected showNoDataWarning(): void {
    this.confirmationService.confirm({
      message: 'No records found for the specified selection criteria.',
      header: 'Warning',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Ok',
      rejectVisible: false,
      acceptButtonStyleClass: 'p-button-info wal-info p-button-rounded',
      key: 'client-dashboard-warning',
      accept: () => { return; }
    });
  }
}
